import React from 'react';

import styled from '@emotion/styled';
import {spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Search} from '../../../icons/Search';
import {Hints} from '../Hints/Hints';

import {useSearchField} from './useSearchField';
import {
    getBaseVariables,
    getDisabledVariables,
    getFocusVariables,
    getHoverVariables
} from './utils';

import type {ValidationFunc} from '@nfq/react-form';

/**
 * `ComponentProps` defines the props for the `SearchField` component, which provides an input field
 * with customizable properties like disabling the field, setting a placeholder, and handling input changes.
 */
export interface ComponentProps {
    /**
     * Indicates whether the input field should be disabled, making it non-interactive.
     *
     * @default false
     */
    isDisabled?: boolean;
    /**
     * The unique name identifier for the search field within the form.
     */
    name?: string;
    /**
     * Callback function triggered when the value in the input field changes.
     * It receives the new value as a string.
     */
    onChange?(value: string): void;
    /**
     * The placeholder text that appears when the input field is empty, providing a hint to the user.
     */
    placeholder: string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'SearchField'
     */
    testId?: string;
    /**
     * Visual theme variant that affects color scheme, either 'light' or 'dark'.
     */
    theme?: 'dark' | 'light';
    /**
     * Optional validation function to validate the input value.
     */
    validation?: ValidationFunc;
}

/**
 * `SearchField` component provides an input field with an optional placeholder and handles user input changes.
 * It also supports disabling the input and allows customization of the visual style.
 * The input value is captured through the `onChange` handler, and an icon is displayed inside the input wrapper.
 *
 * @param props             The component props.
 * @param props.isDisabled  A boolean flag to disable the input field.
 * @param props.onChange    Callback function triggered when the input value changes.
 * @param props.placeholder The placeholder text to display when the input field is empty.
 * @param props.style       Defines the style theme of the input field, either 'dark' or 'light'.
 * @param props.testId      A unique identifier for testing purposes, used to target the component in tests.
 * @returns A JSX element that represents a search input field with an icon and customizable properties.
 *
 * @example
 * ```tsx
 * <SearchField
 *    placeholder="Search here"
 *    onChange={(value) => console.log(value)}
 *    testId="custom-search-field"
 * />
 * ```
 */
const SearchField = observer(({
    isDisabled = false,
    name = 'search',
    onChange = () => {},
    placeholder,
    testId = 'SearchField',
    theme = 'light',
    validation = () => null
}: ComponentProps) => {
    const {error, handleChange, value} = useSearchField(name, '', false, onChange, validation);

    return (
        <InputErrorWrapper data-cy={testId}>
            <IconInputWrapper
                data-has-error={error !== null && error.length > 0}
                data-theme={theme}
            >
                <Input
                    data-cy={`${testId}-input-field`}
                    disabled={isDisabled}
                    name={name}
                    placeholder={placeholder}
                    type="text"
                    value={value}
                    onChange={handleChange}
                />
                <SearchIcon />
            </IconInputWrapper>
            <Hints baseTestId={testId} hints={error} hintType="error" />
        </InputErrorWrapper>
    );
});

SearchField.displayName = 'SearchField';

export {SearchField};

const InputErrorWrapper = styled.div``;

const IconInputWrapper = styled.div`
    ${getBaseVariables};

    position: relative;
    width: 100%;

    &:hover {
        ${getHoverVariables};
    }

    &:has(input:focus-visible) {
        ${getFocusVariables};
    }

    &:has(input[disabled]) {
        ${getDisabledVariables};
    }
`;

const Input = styled.input`
    background-color: var(--bg-color);
    border-color: var(--border-color);
    border-radius: 10rem;
    border-style: solid;
    border-width: 1px;
    color: var(--font-color);
    font-family: ${({theme}) => theme.fonts.OnAir};
    font-size: 1.6rem;
    height: 4rem;
    line-height: 2.6rem;
    outline: none;
    padding: ${spacing(2)} ${spacing(4)} ${spacing(2)} ${spacing(6)};
    transition: background-color 0.3s ease-in-out, border-color 0.3s ease-in-out;
    width: 100%;

    &::placeholder {
        color: ${({theme}) => theme.colors.textSecondary};
    }

    &:disabled {
        cursor: not-allowed;
    }
`;

const SearchIcon = styled(Search)`
    height: 2.4rem;
    position: absolute;
    right: ${spacing(4)};
    top: 50%;
    transform: translateY(-50%);
    transition: color 0.3s ease-in-out;
    width: 2.4rem;

    path {
        fill: var(--icon-color);
        transition: fill 0.3s ease-in-out;
    }
`;